import React from 'react';

// Drop-in replacement for react-bootstrap <Button>. Mirrors variant/size/as props.
export type Variant =
  | 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'link'
  | 'outline-primary' | 'outline-secondary' | 'outline-success' | 'outline-danger'
  | 'outline-warning' | 'outline-info' | 'outline-dark' | 'outline-light';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'lg';
  active?: boolean;
  as?: React.ElementType;
  // anchor / react-router passthrough for `as="a"` or `as={Link}`
  href?: string;
  target?: string;
  rel?: string;
  download?: boolean | string;
  to?: string;
  /**
   * Why the button is disabled. Shown as the tooltip and announced to screen readers while
   * `disabled` is set; put it in `Modal.Footer disabledReason` too so touch users can read it.
   */
  disabledReason?: string;
}

/**
 * True inside `Modal.Footer`. A solid `secondary` button there becomes an outline so the
 * secondary action never competes with the solid primary (W-L9).
 */
export const ModalFooterContext = React.createContext(false);

const VARIANTS: Record<Variant, string> = {
  primary: 'tw-bg-primary tw-text-white tw-border-primary hover:tw-bg-primary-dark',
  secondary: 'tw-bg-content-muted tw-text-white tw-border-content-muted hover:tw-brightness-95',
  success: 'tw-bg-success tw-text-white tw-border-success hover:tw-brightness-95',
  danger: 'tw-bg-danger tw-text-white tw-border-danger hover:tw-brightness-95',
  warning: 'tw-bg-[#ffc107] tw-text-black tw-border-[#ffc107] hover:tw-brightness-95',
  info: 'tw-bg-[#0dcaf0] tw-text-black tw-border-[#0dcaf0] hover:tw-brightness-95',
  light: 'tw-bg-surface-alt tw-text-content tw-border-line hover:tw-bg-surface-hover',
  dark: 'tw-bg-content tw-text-white tw-border-content hover:tw-brightness-125',
  link: 'tw-bg-transparent tw-text-primary tw-border-transparent hover:tw-underline',
  'outline-primary': 'tw-bg-transparent tw-text-primary tw-border-primary hover:tw-bg-primary hover:tw-text-white',
  'outline-secondary': 'tw-bg-transparent tw-text-content-secondary tw-border-content-muted hover:tw-bg-surface-hover',
  'outline-success': 'tw-bg-transparent tw-text-success tw-border-success hover:tw-bg-success hover:tw-text-white',
  'outline-danger': 'tw-bg-transparent tw-text-danger tw-border-danger hover:tw-bg-danger hover:tw-text-white',
  'outline-warning': 'tw-bg-transparent tw-text-[#997404] tw-border-[#ffc107] hover:tw-bg-[#ffc107] hover:tw-text-black',
  'outline-info': 'tw-bg-transparent tw-text-[#087990] tw-border-[#0dcaf0] hover:tw-bg-[#0dcaf0] hover:tw-text-black',
  'outline-dark': 'tw-bg-transparent tw-text-content tw-border-content hover:tw-bg-content hover:tw-text-white',
  'outline-light': 'tw-bg-transparent tw-text-white tw-border-white/60 hover:tw-bg-white hover:tw-text-content',
};

// Standardized sizes — each has a FIXED min-height so every button of a size is the same
// height regardless of content (icon-only vs text) or screen. Consistent across the app.
//   sm  = 32px  (dense toolbars/table rows)
//   md  = 40px  (default — comfortable on desktop, tappable on touch)
//   lg  = 48px  (primary CTAs)
const SIZES = {
  sm: 'tw-min-h-[2rem] tw-px-2.5 tw-py-1 tw-text-sm',
  lg: 'tw-min-h-[3rem] tw-px-5 tw-py-2.5 tw-text-base',
} as const;
const SIZE_DEFAULT = 'tw-min-h-[2.5rem] tw-px-3.5 tw-py-1.5 tw-text-sm';

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size, active, as, className = '', type = 'button', disabledReason, children, ...rest }, ref) => {
    const Comp: React.ElementType = as || 'button';
    const inFooter = React.useContext(ModalFooterContext);
    if (inFooter && (variant === 'secondary' || variant === 'light' || variant === 'dark')) variant = 'outline-secondary';
    const reason = rest.disabled && disabledReason ? disabledReason : undefined;
    const cls = [
      // leading-tight + whitespace-nowrap keep the label on one line so height stays fixed
      'tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded tw-border tw-font-medium tw-leading-tight tw-align-middle',
      'tw-transition disabled:tw-opacity-65 disabled:tw-cursor-not-allowed focus:tw-outline-none focus:tw-shadow-focus',
      VARIANTS[variant] || VARIANTS.primary,
      size ? SIZES[size] : SIZE_DEFAULT,
      active ? 'tw-brightness-95' : '',
      className,
    ].join(' ');
    const extra = Comp === 'button' ? { type } : {};
    return (
      <Comp ref={ref} className={cls} {...extra} {...rest} title={reason ?? rest.title}>
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'lg';
  vertical?: boolean;
}
export const ButtonGroup: React.FC<ButtonGroupProps> = ({ vertical, className = '', children, ...rest }) => (
  <div
    role="group"
    className={`tw-inline-flex ${vertical ? 'tw-flex-col' : 'tw-flex-row'} [&>*:not(:first-child)]:tw-rounded-l-none [&>*:not(:last-child)]:tw-rounded-r-none ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export default Button;
